import React from 'react';
import ReactAudioPlayer from 'react-audio-player';
import {calculateAccuracy, getNonFlaggedErrors} from '../Reader/ReaderLibrary';
import { saveScoredAssessment, paginateStory, getScoredStory } from "../../services/API";
import { makeStringPosessive } from "../../services/util";
import Tour from 'reactour';
import RunningRecord from '../Teacher/RunningRecord';
import Loading from '../Global/Loading';
import {assignmentTypes, earlyReaderContentTagsNoEdm} from '../../constants/constants';
import { statusType } from "../../services/displayStatus";
import studentRecordService from '../../services/StudentRecordService';
import WaitModal from '../Reader/WaitModal';
import {COPY_STRINGS} from '../../constants/translatedCopy';
import Sidebar from './CorrectionSidebar'
import Tooltip from "rc-tooltip";
import FAQModal from './FAQModal';
import moment from 'moment'
import { getUserData } from '../../services/API';

import "rc-tooltip/assets/bootstrap_white.css";
import { GetBrandedTutorName, GetBrandedAppName } from '../../services/BrandingService';
import { useTranslation, withTranslation } from 'react-i18next';
import isAudioDisabledDistrict from '../Reports/services/licensing/studentAudioLicense';

class CorrectionManager extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      scoringComplete: true,
      scoredStory: {
        audio: {
          src: null,
        },
        updatedAt: new Date(),
        student: {},
      },
      errors: [[]],     // the updated list of errors for this assessment
      squashedErrors: [],
      stats: {
        selfCorrections: [],
        skips: [],
        mispronunciations: [],
      },
      wcpmScore: "--",       // the current wcpm score to display at the top. TODO: replace this with a more meaningful metric
      accuracyScore: "--",
      showPreReaderActivities: false,
      isNonEdmActivity: false,
      modified: false,    // whether or not the errors have been modified since the last save
      isTourOpen: false,  // whether or not in context help is currently open
      demoClick: null,    // the callback function passed up from <Word/> to toggle an example word during the app tour
      readerPhrases: [],
      selectedFaq: null,
      phraseCorrectionErrors: []
    }

    this.excludeAudio = isAudioDisabledDistrict();

    this.saveAssessment = this.saveAssessment.bind(this);
    this.setSelectedFaq = this.setSelectedFaq.bind(this);
    this.setErrors = this.setErrors.bind(this);
    this.setErrorsFromReader = this.setErrorsFromReader.bind(this);
    this.setPhraseErrors = this.setPhraseErrors.bind(this);
    this.setDemoClick = this.setDemoClick.bind(this);
    this.closeTour = this.closeTour.bind(this);
    this.setStats = this.setStats.bind(this);
    this.getFaqCodes = this.getFaqCodes.bind(this);
    this.getStatus = this.getStatus.bind(this);
    this.getPercentFlaggedWords = this.getPercentFlaggedWords.bind(this);
    this.arePhraseErrorCountsInvalid = this.arePhraseErrorCountsInvalid.bind(this);
  }

  componentDidMount(){
    const { activityId } = this.props.match.params;

    if(activityId){
      getScoredStory(activityId).then((scoredStory) => {

        //account for instances where the error array is broken apart incorrectly
        let squashedErrors = this.squashErrors(scoredStory.errors);
        let unSquashedErrors = this.unSquashErrors(squashedErrors, scoredStory);

        this.setState({
          scoredStory,
          errors: unSquashedErrors,
          squashedErrors: squashedErrors,
          phraseBreaks: this.getPhraseBreaks(scoredStory.errors),
          wcpmScore: scoredStory.wcpmScore,
          showPreReaderActivities: getUserData().prereaderAudioEnabled === true ? true : false,
          isNonEdmActivity: earlyReaderContentTagsNoEdm.some(tag => scoredStory.story.tags.includes(tag)),
          accuracyScore: calculateAccuracy(getNonFlaggedErrors([scoredStory.errors],scoredStory.flaggedPhrases)),
          readerPhrases: paginateStory(scoredStory.squashedStory).pages[0],
          stats: {
            selfCorrections: scoredStory.selfCorrections || [],
            skips: scoredStory.skips || [],
            mispronunciations: scoredStory.mispronunciations || [],
          },
          flaggedWords: this.getFlaggedWords(scoredStory.errors,scoredStory.flaggedPhrases),
          flaggedWordsInPhrases: this.getFlaggedWordsByPhrase(scoredStory.errors,scoredStory.flaggedPhrases),
          flaggedPhrases: JSON.parse(JSON.stringify(scoredStory.flaggedPhrases))
        });
      });
    }
  }

  getFaqCodes(){
    if(!this.state.scoredStory.studentId) return [];
    let codes = [];
    const {flaggedPhrases, timeRead, timeReadClipped, errors, type } = this.state.scoredStory;

    const statusId = this.state.scoredStory.sidebarStatus.id
    const studentIsEarlyReader = statusId === "PRE-READER";
    const percentFlaggedWords = this.getPercentFlaggedWords();
    const isNonEdmActivity = earlyReaderContentTagsNoEdm.some(tag => this.state.scoredStory.story.tags.includes(tag));

    let nWords = 0;
    let nCorrect = 0;
    for (const phrase of errors) {
      nWords += phrase.length;
      nCorrect += phrase.filter(x => !x).length;
    }

    let combined = [];
    for (let i = 0; i < errors.length; i++)
      combined = combined.concat(errors[i]);
    let res = this.getConsecutive(combined);
    const hasFlaggedPhrases = flaggedPhrases.filter(x => x.triggered).length > 0

    /**
     * the order of these faqs should be as follows:
     * [Under Review, Reassess, Unscoreable Test, Early Next Button, Not Trying, Minimum Scoring, Looks Wrong, Yellow Flag, Inconsistent Time, Yellow Flag Practice, Default]
     */
    const isAssessment = this.state.scoredStory.type == assignmentTypes.ASSESSMENT;
    if(statusId === 'UNDER_REVIEW') codes.push('under_review');
    if(!studentIsEarlyReader && isAssessment && (this.state.accuracyScore < 40 || nWords < 20)) codes.push('reassess');
    if(studentIsEarlyReader) codes.push('unscoreable_test');
    if(res) codes.push('early_next_button');
    if(!studentIsEarlyReader && (this.state.accuracyScore <= 30 || timeRead/1000/nWords > 5)) codes.push('not_trying');
    if(!studentIsEarlyReader && nCorrect < 10 && !isNonEdmActivity) codes.push('minimum_scoring');
    if(percentFlaggedWords > 10 || studentIsEarlyReader) codes.push('looks_wrong');
    if(hasFlaggedPhrases && type !== 'tutor') codes.push('yellow_flag');
    if(!(statusId === 'UNDER_REVIEW') && timeReadClipped && (timeRead - timeReadClipped > 3000)) codes.push('inconsistent_time');
    if(hasFlaggedPhrases && type === 'tutor') codes.push('yellow_flag_practice');
    if(!studentIsEarlyReader) codes.push('default'); //default faq comes last
    return codes;
  }

  getConsecutive(words) {
    let count = 0;
    for (let i = 0; i < words.length; i++) {
      if (words[i] !== true) count = 0;
      count += 1;
      if (10 <= count) return true;
    }
    return false;
  }

  getPercentFlaggedWords(){
    const {flaggedWords} = this.state;
    const totalFlaggedWords = flaggedWords.filter(wordFlag => wordFlag === true).length;
    const totalWords = flaggedWords.length;
    return Math.round((totalFlaggedWords/totalWords)*100);
  }

  getStatus(){
    if(!this.state.scoredStory.studentId) return {};
    let status = {id: this.state.scoredStory.sidebarStatus.id};
    const {flaggedPhrases} = this.state.scoredStory;
    const studentIsEarlyReader = status.id === "PRE-READER";
    status.primary = this.state.scoredStory.sidebarStatus.label;
    if(studentIsEarlyReader) status.primary = "Pre-Reader";
    if(flaggedPhrases.filter(x => x.triggered).length > 0) status.secondary = "Yellow Flagged Activity";
    return status;
  }

  getFlaggedWords(errors,flaggedPhrases){
    if(!errors || !flaggedPhrases){
      return [];
    }
    let flaggedWords = [];
    let overallIndex = 0;

    errors.map((phraseErrors,index)=>{
      for(let wordIndex=0; wordIndex<phraseErrors.length; wordIndex++){
        flaggedWords[overallIndex] = (flaggedPhrases[index] && flaggedPhrases[index].triggered);
        overallIndex++;
      }
    });
    return flaggedWords;
  }

  getFlaggedWordsByPhrase(errors,flaggedPhrases){
    if(!errors || !flaggedPhrases){
      return [[]];
    }

    let flaggedWords = errors.map((phraseErrors,index)=>{
      let phraseFlags = [];
      for(let wordIndex=0; wordIndex<phraseErrors.length; wordIndex++){
        phraseFlags[wordIndex] = (flaggedPhrases[index] && flaggedPhrases[index].triggered);
      }
      return phraseFlags;
    });
    return flaggedWords;
  }

  //returns an error array with only one set of errors to match the squashed story
  squashErrors(errors){
    if(!errors){
      return [[]];
    }
    let newErrors = [];
    for(let i = 0; i < errors.length; i++){
      newErrors = newErrors.concat(errors[i]);
    }
    return [newErrors];
  }

  // TODO: fix error storage / phrase management so that this is not necessary
  unSquashErrors(errors, scoredStory=this.state.scoredStory){
    if(!errors){
      return [[]];
    }
    let remainingErrors = errors[0];
    let unSquashedErrors = [];
    for(let i = 0; i < scoredStory.phrases.length; i++){
      let phrase = scoredStory.phrases[i].text;
      let length = phrase.split(' ').length;
      unSquashedErrors.push(remainingErrors.slice(0, length));
      remainingErrors = remainingErrors.slice(length, remainingErrors.length);
    }
    return unSquashedErrors;
  }

  getPhraseBreaks(errors){
    if(!errors){
      return [];
    }
    let overallLength = 0;
    return errors.map((phrase)=>{
      overallLength += phrase.length;
      return overallLength;
    });
  }

  setErrorsFromReader(newErrors,newFlags,index){
    let phraseFlags = this.state.flaggedPhrases;

    //Once a word in the corrected phrase is hit, we mark the whole phrase as corrected
    if(phraseFlags.length > 0){
      let phrase=0;
      for(phrase=0;phrase<this.state.phraseBreaks.length;phrase++){
        if(index < this.state.phraseBreaks[phrase]) break;
      }

      if(phraseFlags[phrase]){
        phraseFlags[phrase].triggered = false;
      }
    }

    //from reader we have to index in to page 0 and then unsquash the errors before saving them
    this.setErrors(this.unSquashErrors([newErrors]),phraseFlags);

    this.setState({flaggedWords: this.getFlaggedWords(this.state.scoredStory.errors,phraseFlags), flaggedPhrases: phraseFlags});
  }

  // keep an updated copy of the errors for this assessment as the teacher makes changes
  setErrors(newErrors, flaggedPhrases, callback=null){
    let wrappedErrors = [newErrors];
    let squashedErrors = this.squashErrors(newErrors);
    this.setState({
      errors: newErrors,
      wcpmScore: "--",
      accuracyScore: calculateAccuracy(getNonFlaggedErrors(wrappedErrors,flaggedPhrases)),
      squashedErrors: squashedErrors,
      modified: true,
    }, callback);
  }

  setPhraseErrors(newPhraseErrors, phraseIndex, callback=null){
    let newErrors = this.state.errors;
    newErrors[phraseIndex] = newPhraseErrors;


    let phraseFlags = this.state.flaggedPhrases;

    //Once a word in the corrected phrase is hit, we mark the whole phrase as corrected
    if(phraseFlags){
      if(!phraseFlags[phraseIndex]){
        phraseFlags[phraseIndex] = {
          triggered: false,
        }
      }else{
        phraseFlags[phraseIndex].triggered = false;
      }
    }

    this.setErrors(newErrors, phraseFlags, callback);
    this.setState({
      flaggedWordsInPhrases: this.getFlaggedWordsByPhrase(this.state.scoredStory.errors,phraseFlags),
      flaggedPhrases: phraseFlags
    });
  }

  setStats(newStats, callback){
    this.setState({
      stats: newStats,
      modified: true,
    },() => {
      callback();
      this.arePhraseErrorCountsInvalid();
    });
  }

  //set the function to demo a word toggle in the tour
  setDemoClick(demoClickFunction){
    this.setState({
      demoClick: demoClickFunction,
    });
  }

  onDoneWaiting(){
    this.setState({modified: false});
    this.props.closeModal();
  }

  openWaitModal(timeout=10000){
    window.setTimeout(() => this.setState({scoringComplete: true}), 10000); //even if scoring is not complete, act as though it is after 10 seconds
    this.props.openModal(
      <WaitModal
        doneWaiting={this.isActivityScored.bind(this)}
        continue={this.onDoneWaiting.bind(this)}
        messageText = {COPY_STRINGS.scoring_wait_modal_text}
      />
    );
  }

  setSelectedFaq(selectedFaq){
    this.setState({selectedFaq});
  }

  arePhraseErrorCountsInvalid(){
    let phraseErrorValidations = [];
    this.state.errors.forEach((errors, index) => {
      let totalErrors = errors.filter(x => x).length;
      let skipsAndMiscues = (this.state.stats.skips[index] || 0) + (this.state.stats.mispronunciations[index] || 0);
      if (skipsAndMiscues <= totalErrors) phraseErrorValidations.push(false);
      else phraseErrorValidations.push(true);
    })
    this.setState({phraseCorrectionErrors: phraseErrorValidations});
    return phraseErrorValidations;
  }

  async saveAssessment(){
    let timeout = 10000;
    if(!getUserData().demoLockdown){
      //Update any flagged phrases that have been rescored
      let hasPhraseCorrectionErrors = this.state.phraseCorrectionErrors.filter(x => x).length > 0;
      if(hasPhraseCorrectionErrors || getUserData().demoLockdown) return;
      if(this.state.flaggedPhrases){
        this.state.flaggedPhrases.map(async (phraseFlag,index)=>{
          if(this.state.scoredStory.flaggedPhrases[index].triggered !== phraseFlag.triggered){
            await studentRecordService.updateFlaggedPhrase(this.state.scoredStory.activityId,index,phraseFlag.triggered);
          }
        });
      }

      const activityId = this.state.scoredStory.activityId
      saveScoredAssessment(activityId, this.state.errors, this.state.stats.skips, this.state.stats.selfCorrections, this.state.stats.mispronunciations, this.state.scoredStory.timeRead, false).then(() => {
        setTimeout(() => {
          studentRecordService.getActivity(activityId, this.excludeAudio).then(res => {
            const activity = res.data.getActivity[0]
            const {wcpmScore} = activity.scores
            this.setState({wcpmScore})
          })
        }, 5000)
      })
    }else{
      timeout = 2000;
    }

    this.setState({
      scoringComplete: false,
    }, this.openWaitModal.bind(this,timeout));
  }

  closeTour(){
    this.setState({
      isTourOpen: false,
    });
  }

  isActivityScored(){
    // doing it this way instead of just passing the state through as a prop and doing the polling logic here in order to make use of the logic in WaitModal
    if(!this.state.scoringComplete){
      const { activityId } = this.props.match.params;
      studentRecordService.getActivity(activityId, this.excludeAudio).then((response) => {
        if(!response || !response.data || !response.data.getActivity || !response.data.getActivity[0] || !response.data.getActivity[0].status){
          return;
        }
        let status = response.data.getActivity[0].status;
        this.setState({scoringComplete: status == statusType.SCORED});
      });
    }
    return this.state.scoringComplete;
  }

  // returns an array of tour step objects for reacttour. Currently this code is not reachable because this.state.isTourOpen is set to false.
  // TODO: finish tour
  getTourSteps(){
    const steps = [
      {
        selector: '',
        content: () => (
          <div className="helpContent">
            {`Welcome! You are currently grading ${makeStringPosessive(this.state.scoredStory.studentName)} assessment. Click through this tour to see how to use ${GetBrandedAppName()} to correct the score`}
          </div>
        ),
      },
      {
        selector: '[data-tut="audio"]',
        content: () => (
          <div className="helpContent">
            {"Use the audio player to listen to the recording of " + makeStringPosessive(this.state.scoredStory.studentName) + " assessment."}
          </div>
        ),
      },
      {
        selector: '[data-tut="word"]',
        content: () => (
          <div className="helpContent">
            If you find a word that {GetBrandedTutorName()} has scored incorrectly, click the word to toggle the score
          </div>
        ),
      },
      {
        selector: '[data-tut="key"]',
        content: () => (
          <div className="helpContent">
            Green means correct, red means incorrect, and grey means that the student did not reach this part of the passage
          </div>
        ),
      },
      {
        selector: '[data-tut="score"]',
        content: () => (
          <div className="helpContent">
            As you correct the assessment, you will see the wcpm score change in real time
          </div>
        ),
      },
      {
        selector: '[data-tut="save"]',
        content: () => (
          <div className="helpContent">
            When you have finished scoring the assessment, click 'save' to save your changes.
          </div>
        ),
      },
    ];
    return steps;
  }

  getCorrectionUI(allowRescoring = true){
    return <RunningRecord
            setErrors={this.setPhraseErrors}
            errors={this.state.errors}
            scoredStory={this.state.scoredStory}
            stats={this.state.stats}
            setStats={this.setStats}
            phraseCorrectionErrors={this.state.phraseCorrectionErrors}
            flaggedWords={this.state.flaggedWordsInPhrases}
            activityId={this.props.match.params.activityId}
            allowRescoring={allowRescoring}
          />          
  }

  render(){
    const {t} = this.props;
    let loading = this.state.readerPhrases.length <= 0;
    const n = 35 // ellipses threshold
    const isAssessment = this.state.scoredStory.type == assignmentTypes.ASSESSMENT;
    const isLetterNaming = this.state.scoredStory.type == assignmentTypes.LETTER_NAMING_AND_SOUNDS;
    let allowRescoring = true;
    const { allowLetterNamingAndSoundsRescoring } = getUserData();
    if (isLetterNaming && !allowLetterNamingAndSoundsRescoring) allowRescoring = false;
    if (this.excludeAudio) allowRescoring = false;

    // choose the header text. use the student's name if we have it, and the date if we don't
    let correctionUI = this.getCorrectionUI(allowRescoring);

    let headerText = t('Scoring Reading Activity');   
    if (!loading) {
      let studentName = ""
      const student = this.state.scoredStory.student
      if (student) {
        studentName = `${student.firstName} ${student.lastName}`;
        studentName = studentName.length <= n ? studentName : studentName.slice(0,n-3) + "...";
      }
      let activityType = t(isAssessment ? "common:Assessment" : "common:Practice");
      if (isLetterNaming) {
        activityType = t("Letter Names and Sounds");
      }
      const [month, day] = moment(this.state.scoredStory.createdAt).format("M/DD").split("/")
      headerText = t("correction_header", {studentName, activityType, month, day})
    }

    const status = this.getStatus()
    const statusId = status.id
    let blockAudio = false
    if (statusId === 'UNDER_REVIEW' || (statusId === 'PRE-READER' && this.state.showPreReaderActivities == false)) blockAudio = true

    const rescoreMessage = !allowRescoring && !this.excludeAudio ? t("Letter Names and Sounds Rescoring Warning") : null;

    return (
      <div className='scoring-container'>
        <div className="correction">
        <FAQModal
          selectedFaq={this.state.selectedFaq}
          setSelectedFaq={this.setSelectedFaq}
        />
          <div className="header">
            {headerText}
          </div>
          {rescoreMessage &&
            <div className="rescoring-warning-message">
              {rescoreMessage}
            </div>
          }
          <div className="key" data-tut="key">
            <ToolTipContainer placement="bottom" type="correct">
              <div className="label">
                <span className="dot correct"></span>
                <span className="label">{t("Correct")}</span>
              </div>
            </ToolTipContainer>
            <ToolTipContainer placement="bottom" type="incorrect">
              <div className="label">
                <span className="dot error"></span>
                <span className="label">{t("Incorrect")}</span>
              </div>
            </ToolTipContainer>
            <ToolTipContainer placement="bottom" type="not_read">
              <div className="label">
                <span className="dot unread"></span>
                <span className="label">{t("Not Read")}</span>
              </div>
            </ToolTipContainer>
            <ToolTipContainer placement="bottom" type="flagged">
              <div className="label">
                <span className="dot flagged"></span>
                <span className="label">{t("Flagged")}</span>
              </div>
            </ToolTipContainer>
          </div>
          {loading ? <Loading /> : correctionUI}
          {!this.excludeAudio && <div className="audioWrapper" data-tut="audio">
            <ReactAudioPlayer
              src={blockAudio ? null: this.state.scoredStory.audio.url}
              title={true}
              controls
            />
          </div>}
          <Tour
            steps={this.getTourSteps()}
            isOpen={this.state.isTourOpen}
            onRequestClose={this.closeTour}
            rounded={5}
          />
        </div>
        <Sidebar
          isNonEdmActivity={this.state.isNonEdmActivity}
          modified={this.state.modified}
          saveAssessment={this.saveAssessment}
          accuracyScore={this.state.accuracyScore}
          phraseCorrectionErrors={this.state.phraseCorrectionErrors}
          wcpmScore={this.state.wcpmScore}
          readTime={this.state.scoredStory.timeReadClipped || this.state.scoredStory.timeRead}
          faqs={this.getFaqCodes()}
          status={status}
          setSelectedFaq={this.setSelectedFaq}
          isAssessment={isAssessment}
          activity={this.state.scoredStory}
        />
      </div>
    );
  }
}

export { ToolTipContainer }

function ToolTipContainer(props){
  const {t} = useTranslation("review_activity");

  let trigger = props.disabled ? [] : ['hover'];
  let overlayStyle = {width: 200, background: 'white' };
  if(props.disabled) overlayStyle.display = 'none';

  const getTooltipContent = (type) => {
    let tooltipContent = "";
    if(type == "correct"){
      tooltipContent = t("tooltip_correct");
    }
    if(type == "incorrect"){
      tooltipContent = t("tooltip_incorrect");
    }
    if(type == "not_read"){
      tooltipContent = t("tooltip_not_read");
    }
    if(type == "omitted"){
      tooltipContent = `The student skipped or did not read the word.
        This is scored as incorrect.`;
    }
    if(type == "flagged"){
      tooltipContent = t("tooltip_flagged");
    }
    if(type == "accuracy"){
      tooltipContent = t("tooltip_accuracy");
    }
    if(type == "adj_wcpm"){
      tooltipContent = t("tooltip_adj_wcpm");
    }
    if(type == "error"){
      tooltipContent = t("tooltip_error");
    }
    if(type == "self-corrections"){
      tooltipContent = t("tooltip_self_corrections");
    }
    if(type == "skips"){
      tooltipContent = t("tooltip_skips");
    }
    if(type == "miscues"){
      tooltipContent = t("tooltip_miscues");
    }
    if(type == "time_in_play_button"){
      tooltipContent = `This is the length of the audio.  It does not
        necessarily reflect the time used to calculate
        adjusted WCPM.  Remember: recordings only
        include the student reading, not ${GetBrandedTutorName()}'s
        coaching.`;
    }
    if(type == "phrase_correction_error"){
      tooltipContent = `The number of skips and miscues cannot be greater than the number of errors.`;
    }
    return tooltipContent;
  }

  return (
    <Tooltip
      placement={props.placement}
      trigger={trigger}
      overlayStyle={overlayStyle}
      overlay={<p className="correction-manager-tooltip-content">
        {getTooltipContent(props.type)}
      </p>}
    >{props.children}
    </Tooltip>
  );
}

export default withTranslation("review_activity")(CorrectionManager);